import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { Layout } from "../components/Layout";
import { ArrowRight, Plus, X } from "@phosphor-icons/react";
import api from "../lib/api";
import { toast, Toaster } from "sonner";

export default function Onboarding() {
  const { user, setUser } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [skills, setSkills] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [headline, setHeadline] = useState("");
  const [bio, setBio] = useState("");
  const [yrs, setYrs] = useState(0);
  const [education, setEducation] = useState("");
  const [availability, setAvailability] = useState("gig");
  const [companyName, setCompanyName] = useState("");
  const [orgName, setOrgName] = useState("");
  const [industry, setIndustry] = useState("");
  const [selectedSectors, setSelectedSectors] = useState([]);
  const [teamSize, setTeamSize] = useState(1);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/skills").then(({ data }) => {
      setSkills(data.skills);
      setSectors(data.sectors);
    });
  }, []);

  if (!user) return null;
  const role = user.role;

  const toggle = (arr, setArr, v) => {
    setArr(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { headline, bio };
      if (role === "skilled_individual") {
        Object.assign(payload, { skills: selectedSkills, years_experience: Number(yrs), education, availability });
      } else if (role === "msme") {
        Object.assign(payload, { company_name: companyName, sectors: selectedSectors, skills: selectedSkills, team_size: Number(teamSize) });
      } else if (role === "employer") {
        Object.assign(payload, { organization_name: orgName, industry });
      } else if (role === "sponsor") {
        Object.assign(payload, { organization_name: orgName, industry: "Sponsor", sponsor_type: industry || "corporate_csr" });
      }
      const { data } = await api.put("/profile", payload);
      setUser(data);
      toast.success(t("profile_saved"));
      nav(role === "sponsor" ? "/sponsor/dashboard" : "/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error_occurred"));
    } finally { setBusy(false); }
  };

  return (
    <Layout>
      <Toaster richColors position="top-center" />
      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="text-xs font-bold uppercase tracking-[0.18em] text-[var(--as-accent)]">{t("get_started")}</div>
        <h1 className="text-3xl font-bold text-[var(--as-primary)] mt-1" style={{fontFamily:"Inter"}}>
          {t("cta_create_profile")}
        </h1>
        <p className="mt-2 text-[var(--as-text-secondary)]">{user.name} · {role.replace("_", " ")}</p>

        <form onSubmit={submit} className="as-card p-7 mt-8 space-y-6" data-testid="onboarding-form">

          {role === "skilled_individual" && (
            <>
              <div>
                <label className="as-label">{t("headline")}</label>
                <input className="as-input" value={headline} onChange={(e) => setHeadline(e.target.value)} placeholder="Master electrician with 10 years exp." data-testid="onb-headline" />
              </div>
              <div>
                <label className="as-label">{t("bio")}</label>
                <textarea className="as-input min-h-[100px]" value={bio} onChange={(e) => setBio(e.target.value)} data-testid="onb-bio" />
              </div>

              <div>
                <label className="as-label">{t("skills")}</label>
                <div className="flex flex-wrap gap-2 max-h-72 overflow-y-auto p-2 border border-[var(--as-border)] rounded-xl bg-[var(--as-bg-alt)]">
                  {skills.map((s) => (
                    <button key={s} type="button" onClick={() => toggle(selectedSkills, setSelectedSkills, s)}
                      className={`as-badge-skill ${selectedSkills.includes(s) ? "!bg-[var(--as-primary)] !text-[var(--as-bg)] !border-[var(--as-primary)]" : ""}`}
                      data-testid={`onb-skill-${s.toLowerCase().replace(/\s+/g, '-')}`}>
                      {selectedSkills.includes(s) ? <X size={12} weight="bold" /> : <Plus size={12} weight="bold" />} {s}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="as-label">{t("years_experience")}</label>
                  <input className="as-input" type="number" min={0} max={60} value={yrs} onChange={(e) => setYrs(e.target.value)} data-testid="onb-years" />
                </div>
                <div>
                  <label className="as-label">{t("availability")}</label>
                  <select className="as-input" value={availability} onChange={(e) => setAvailability(e.target.value)} data-testid="onb-avail">
                    <option value="full_time">{t("full_time")}</option>
                    <option value="part_time">{t("part_time")}</option>
                    <option value="gig">{t("gig")}</option>
                    <option value="remote">{t("remote")}</option>
                    <option value="on_site">{t("on_site_work")}</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="as-label">{t("education")}</label>
                <input className="as-input" value={education} onChange={(e) => setEducation(e.target.value)} placeholder="ITI Electrician, NSQF Level 4" data-testid="onb-edu" />
              </div>
            </>
          )}

          {role === "msme" && (
            <>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="as-label">{t("company_name")}</label>
                  <input className="as-input" value={companyName} required onChange={(e) => setCompanyName(e.target.value)} data-testid="onb-company" />
                </div>
                <div>
                  <label className="as-label">{t("team_size")}</label>
                  <input className="as-input" type="number" min={1} value={teamSize} onChange={(e) => setTeamSize(e.target.value)} data-testid="onb-team" />
                </div>
              </div>
              <div>
                <label className="as-label">{t("bio")}</label>
                <textarea className="as-input min-h-[100px]" value={bio} onChange={(e) => setBio(e.target.value)} data-testid="onb-bio" />
              </div>
              <div>
                <label className="as-label">{t("sectors")}</label>
                <div className="flex flex-wrap gap-2">
                  {sectors.map((s) => (
                    <button key={s} type="button" onClick={() => toggle(selectedSectors, setSelectedSectors, s)}
                      className={`as-badge-skill ${selectedSectors.includes(s) ? "!bg-[var(--as-primary)] !text-[var(--as-bg)] !border-[var(--as-primary)]" : ""}`}
                      data-testid={`onb-sector-${s.toLowerCase().replace(/\s+/g, '-')}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="as-label">{t("skills")}</label>
                <div className="flex flex-wrap gap-2 max-h-60 overflow-y-auto p-2 border border-[var(--as-border)] rounded-xl bg-[var(--as-bg-alt)]">
                  {skills.map((s) => (
                    <button key={s} type="button" onClick={() => toggle(selectedSkills, setSelectedSkills, s)}
                      className={`as-badge-skill ${selectedSkills.includes(s) ? "!bg-[var(--as-primary)] !text-[var(--as-bg)] !border-[var(--as-primary)]" : ""}`}
                      data-testid={`onb-msme-skill-${s.toLowerCase().replace(/\s+/g, '-')}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {role === "employer" && (
            <>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="as-label">{t("organization_name")}</label>
                  <input className="as-input" value={orgName} required onChange={(e) => setOrgName(e.target.value)} data-testid="onb-org" />
                </div>
                <div>
                  <label className="as-label">{t("industry")}</label>
                  <input className="as-input" value={industry} onChange={(e) => setIndustry(e.target.value)} data-testid="onb-industry" />
                </div>
              </div>
              <div>
                <label className="as-label">{t("bio")}</label>
                <textarea className="as-input min-h-[100px]" value={bio} onChange={(e) => setBio(e.target.value)} data-testid="onb-bio" />
              </div>
            </>
          )}

          {role === "sponsor" && (
            <>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="as-label">{t("organization_name")}</label>
                  <input className="as-input" value={orgName} required onChange={(e) => setOrgName(e.target.value)} data-testid="onb-org" />
                </div>
                <div>
                  <label className="as-label">Sponsor type</label>
                  <select className="as-input" value={industry} onChange={(e) => setIndustry(e.target.value)} data-testid="onb-sponsor-type">
                    <option value="corporate_csr">Corporate CSR</option>
                    <option value="foundation">Foundation</option>
                    <option value="brand">Brand</option>
                    <option value="bank">Bank / NBFC</option>
                    <option value="govt">Government</option>
                    <option value="individual_patron">Individual patron</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="as-label">Mission / focus</label>
                <textarea className="as-input min-h-[100px]" value={bio} onChange={(e) => setBio(e.target.value)} placeholder="What CSR / philanthropy themes do you focus on?" data-testid="onb-bio" />
              </div>
            </>
          )}

          <div className="flex justify-end pt-2">
            <button type="submit" disabled={busy} className="btn-primary" data-testid="onb-submit">
              {busy ? "..." : t("save")} <ArrowRight size={18} weight="bold" />
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
